import React from 'react';
import { useTranslation } from 'react-i18next';
import { BudgetSummary as BudgetSummaryType, ContributionCalculationType } from '../types/budget';
import { formatCurrency } from '../utils/budgetCalculations';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

interface BudgetSummaryProps {
  summary: BudgetSummaryType;
  calculationType?: ContributionCalculationType;
}

export const BudgetSummary: React.FC<BudgetSummaryProps> = ({
  summary,
  calculationType = ContributionCalculationType.INCOME_RATIO,
}) => {
  const { t } = useTranslation();

  // Map calculation type to translation key
  const getCalculationTypeLabel = () => {
    switch (calculationType) {
      case ContributionCalculationType.INCOME_RATIO:
        return t('budget.settings.incomeRatio');
      case ContributionCalculationType.EQUAL_SPLIT:
        return t('budget.settings.equalSplit');
      case ContributionCalculationType.AFTER_PERSONAL:
        return t('budget.settings.afterPersonal');
      case ContributionCalculationType.FULL_BALANCE:
        return t('budget.settings.fullBalance');
      default:
        return t('budget.settings.incomeRatio');
    }
  };

  return (
    <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800 sm:p-6">
      <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
        {t('budget.summary.title')}
      </h2>

      {/* Global Summary */}
      <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5">
        <div className="rounded-lg bg-green-50 p-3 dark:bg-green-900/30 sm:p-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('budget.summary.totalIncome')}
          </h3>
          <div className="mt-2 flex items-center gap-1">
            <p className="text-lg font-semibold text-green-900 dark:text-green-100 sm:text-2xl">
              {formatCurrency(summary.totalIncome)}
            </p>
          </div>
        </div>
        <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/30 sm:p-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('budget.summary.personalExpenses')}
          </h3>
          <div className="mt-2 flex items-center gap-1">
            <p className="text-lg font-semibold text-red-600 dark:text-red-400 sm:text-2xl">
              {formatCurrency(summary.totalExpenses)}
            </p>
          </div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50 sm:p-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('budget.summary.balance')}
          </h3>
          <p
            className={`mt-2 text-lg font-semibold sm:text-2xl ${
              summary.balance >= 0
                ? 'text-green-900 dark:text-green-100'
                : 'text-red-900 dark:text-red-100'
            }`}
          >
            {formatCurrency(summary.balance)}
          </p>
        </div>
        <div className="rounded-lg bg-rose-50/50 p-3 dark:bg-rose-900/20 sm:p-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('budget.summary.commonExpenses')}
          </h3>
          <div className="mt-2 flex items-center justify-between">
            <p className="text-lg font-semibold text-fuchsia-600 dark:text-rose-300 sm:text-2xl">
              {formatCurrency(summary.personContributions.common.expenses)}
            </p>
            {/* Calculation method indicator */}
            <div className="group relative">
              <div className="flex items-center justify-center rounded-full p-1 text-rose-700 dark:text-rose-300">
                <InformationCircleIcon className="h-4 w-4" />
              </div>
              <div className="invisible absolute right-0 top-full z-10 mt-2 w-64 rounded-lg bg-white p-3 text-sm shadow-lg transition-all group-hover:visible dark:bg-gray-800">
                <p className="font-medium text-gray-900 dark:text-white">
                  {getCalculationTypeLabel()}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50 sm:p-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('budget.summary.remaining')}
          </h3>
          <div
            className={`mt-2 inline-flex items-center gap-2 rounded-md px-2 py-1 sm:px-3 ${
              Object.values(summary.personContributions)
                .filter((person) => person.availableBalance)
                .reduce((sum, person) => sum + person.availableBalance, 0) >= 0
                ? 'bg-green-50 dark:bg-green-900/30'
                : 'bg-red-50 dark:bg-red-900/30'
            }`}
          >
            <p
              className={`text-lg font-semibold sm:text-2xl ${
                Object.values(summary.personContributions)
                  .filter((person) => person.availableBalance)
                  .reduce((sum, person) => sum + person.availableBalance, 0) >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}
            >
              {formatCurrency(
                Object.values(summary.personContributions)
                  .filter((person) => person.availableBalance)
                  .reduce((sum, person) => sum + person.availableBalance, 0)
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Individual Contributions */}
      <div className="mt-8">
        <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
          {t('budget.summary.individualContributions')}
        </h3>
        <div className="space-y-2">
          {Object.entries(summary.personContributions)
            .filter(([person]) => person !== 'common')
            .map(([person, data]) => (
              <div key={person} className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700/50">
                <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-[100px_1fr_1fr_1fr_auto] xl:grid-cols-[100px_180px_180px_160px_160px_1fr]">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white">{person}</h4>

                  {/* Income */}
                  <div className="flex items-center gap-2">
                    <span className="w-16 text-sm text-gray-500 dark:text-gray-400">
                      {t('budget.summary.income')}:
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-green-600 dark:text-green-400">
                        {formatCurrency(data.income)}
                      </span>
                    </div>
                  </div>

                  {/* Expenses */}
                  <div className="flex items-center gap-2">
                    <span className="w-16 text-sm text-gray-500 dark:text-gray-400">
                      {t('budget.summary.expenses')}:
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-red-600 dark:text-red-400">
                        {formatCurrency(data.expenses)}
                      </span>
                    </div>
                  </div>

                  {/* Balance */}
                  <div className="flex items-center gap-2">
                    <span className="w-16 text-sm text-gray-500 dark:text-gray-400">
                      {t('budget.summary.balance')}:
                    </span>
                    <span
                      className={`text-sm font-medium ${
                        data.balance >= 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {formatCurrency(data.balance)}
                    </span>
                  </div>

                  {/* Contribution */}
                  <div className="flex items-center gap-2">
                    <span className="w-24 text-sm text-gray-500 dark:text-gray-400">
                      {t('budget.summary.contribution')}:
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-fuchsia-600 dark:text-rose-600">
                        {formatCurrency(data.contribution)}
                      </span>
                    </div>
                  </div>

                  {/* Available Balance - Uses media queries for positioning */}
                  <div className="flex justify-start lg:col-start-5 lg:mt-2 xl:col-start-auto xl:mt-0 xl:justify-end">
                    <div
                      className={`flex items-center gap-2 ${
                        data.availableBalance >= 0
                          ? 'rounded-md bg-green-50 px-2 py-1 dark:bg-green-900/30 sm:px-3'
                          : 'rounded-md bg-red-50 px-2 py-1 dark:bg-red-900/30 sm:px-3'
                      }`}
                    >
                      <span className="w-28 text-sm text-gray-500 dark:text-gray-400">
                        {t('budget.summary.availableBalance')}:
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          data.availableBalance >= 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-lg font-bold text-red-600 dark:text-red-400'
                        }`}
                      >
                        {formatCurrency(data.availableBalance)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
